// ============================================================
// ENGINE — ROOM BACKDROP (Gilded Obsidian)
// ============================================================
// "So it's not just black." — caP'n, July 2026
//
// The letterbox space around every walkable room is now a
// COSMETIC SURFACE: unlockable/purchasable backdrops rendered
// behind the room map. Structure first, art overlay later (4xS):
// every backdrop here is pure CSS — gradients + subtle motion —
// so the system is live TODAY and CDN art drops in later as a
// recipe swap, exactly like HoloFoil tiers.
//
// FOUR EXPORTS:
//   BACKDROPS               — the catalog (id, name, cost, rarity,
//                             lore line, css recipe)
//   useEquippedBackdrop(uid)— live listener on the equipped id
//   <RoomBackdrop uid/>     — the letterbox layer. Rooms render it
//                             FIRST and set their own root/frame
//                             backgrounds to "transparent".
//   <BackdropAtelier/>      — browse · preview · purchase · equip.
//                             Reached from the RoomOverlay menu
//                             (Customize → Backdrop Atelier), so
//                             EVERY wrapped room gets it for free.
//
// LAWS HONORED:
//   • Purchases spend Divine Crests through progression.spendCrests
//     (atomic transaction — Terror writes divineCrests only).
//   • Unlocks are APPEND-ONLY at profiles/{uid}/roomBackdrops/
//     unlocked/{id} (transaction-merge, provenance-stamped).
//   • GRANT-BEFORE-FLAG: the unlock write is the grant; equipped
//     is just a pointer — a failed equip never burns a purchase.
//   • Equip-anywhere, appear-everywhere (cosmetic law): ONE
//     equipped backdrop follows the player through every room.
//   • Unique motion per tier — no backdrop is a recolor of another
//     (HoloFoil law, honored in spirit).
//   • Keyframes injected imperatively, id-guarded (Material Law 6).
//
// SCHEMA (new node — add to the hardening-session whitelist):
//   profiles/{uid}/roomBackdrops/
//     equipped : string (backdrop id)
//     unlocked : { [id]: { source, ts } }   ← append-only
// ============================================================

import { useState, useEffect } from "react";
import { GM } from "./theme";
import { ref, onValue } from "firebase/database";
import { db } from "./firebase";
import { unlockBackdrop, equipBackdrop } from "./progression";

// ── Keyframes — imperative, id-guarded ────────────────────────
function ensureBackdropKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("rb-keyframes")) return;
  const el = document.createElement("style");
  el.id = "rb-keyframes";
  el.textContent = `
    @keyframes rbEmberBreathe { 0%,100%{opacity:0.55} 50%{opacity:0.95} }
    @keyframes rbDrift { from{background-position:0% 0%} to{background-position:200% 0%} }
    @keyframes rbCoil { 0%{background-position:50% 0%} 50%{background-position:50% 100%} 100%{background-position:50% 0%} }
    @keyframes rbRain { from{background-position:0 -140px} to{background-position:0 0} }
    @keyframes rbSpin { from{transform:rotate(0deg) scale(1.6)} to{transform:rotate(360deg) scale(1.6)} }
    @keyframes rbPrism { 0%,100%{filter:hue-rotate(0deg);opacity:0.5} 50%{filter:hue-rotate(60deg);opacity:0.85} }
    @keyframes rbSigilSpin { from{transform:rotate(360deg)} to{transform:rotate(0deg)} }
    @keyframes rbSigilPulse { 0%,100%{opacity:0.35;transform:scale(1)} 50%{opacity:0.8;transform:scale(1.1)} }
    @media (prefers-reduced-motion: reduce) {
      [data-rb-motion] { animation: none !important; }
    }
  `;
  document.head.appendChild(el);
}
ensureBackdropKeyframes();

// ── Reward-tier chrome — THE THREE CLASSES (caP'n, July 8) ────
// Every shop reward now belongs to exactly ONE of three classes:
//   FABLE      — common, the everyday drop
//   LEGENDARY  — the mid rung, gilded
//   MYTHIC     — rarest, our best-designed foils. SIGNATURES ARE
//                ALWAYS MYTHIC.
// `rarity` is a pure DISPLAY field — it is NEVER written to
// Firebase (only the backdrop id + provenance + spend are), so
// re-bucketing here is cosmetic-only: no schema break, no
// migration. The six legacy v1.1 keys are kept as ALIASES that
// fold into the three classes, so any old `rarity:` string on a
// catalog entry (or elsewhere) still resolves and nothing crashes.
export const BACKDROP_RARITY = {
  // Canonical three
  fable:     { label: "FABLE",     color: "#b3a68c" },
  legendary: { label: "LEGENDARY", color: "#e8b84b" },
  mythic:    { label: "MYTHIC",    color: "#f43f5e" },
  // Legacy aliases → folded into the three (back-compat only)
  common:    { label: "FABLE",     color: "#b3a68c" },
  uncommon:  { label: "FABLE",     color: "#b3a68c" },
  rare:      { label: "LEGENDARY", color: "#e8b84b" },
  epic:      { label: "LEGENDARY", color: "#e8b84b" },
};

// ── THE CATALOG ───────────────────────────────────────────────
// Each `render(key)` returns the layer stack for that backdrop.
// Unique motion signature per backdrop — never a recolor.
export const DEFAULT_BACKDROP_ID = "obsidian";

export const BACKDROPS = [
  {
    id: "obsidian", name: "Gilded Obsidian", rarity: "fable", cost: 0,
    lore: "The void as the Emperium left it. Free, forever.",
    render: () => (
      <div key="base" style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 40%, #0b0718 0%, #070410 55%, #030208 100%)",
      }} />
    ),
  },
  {
    id: "emberline", name: "Emberline", rarity: "fable", cost: 1,
    lore: "Willie's forge never fully sleeps. Neither does this.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(180deg, #070410 0%, #120708 60%, #1c0a06 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "40%",
        background: "radial-gradient(ellipse at 50% 100%, rgba(249,115,22,0.16), transparent 70%)",
        animation: "rbEmberBreathe 4s ease-in-out infinite" }} />,
    ],
  },
  {
    id: "deep_vellum", name: "Deep Vellum", rarity: "fable", cost: 1,
    lore: "Moonlight through a page nobody finished writing.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(165deg, #0c0a14 0%, #131022 55%, #0a0812 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0, opacity: 0.5,
        background: "linear-gradient(100deg, transparent 30%, rgba(236,226,204,0.05) 50%, transparent 70%)",
        backgroundSize: "200% 100%", animation: "rbDrift 14s linear infinite" }} />,
    ],
  },
  {
    id: "amethyst_drift", name: "Amethyst Drift", rarity: "fable", cost: 1,
    lore: "The Crystal dreamed once. This is what leaked out.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 30% 20%, #170b2e 0%, #0a0616 55%, #050310 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(115deg, transparent 20%, rgba(147,51,234,0.12) 45%, rgba(109,40,217,0.08) 55%, transparent 80%)",
        backgroundSize: "200% 100%", animation: "rbDrift 22s linear infinite" }} />,
    ],
  },
  {
    id: "serpents_coil", name: "Serpent's Coil", rarity: "legendary", cost: 1,
    lore: "Vyen swears her cousins had nothing to do with this.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(180deg, #050c08 0%, #071510 50%, #04100a 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0, opacity: 0.7,
        background: "repeating-linear-gradient(0deg, transparent 0px, rgba(74,222,128,0.05) 40px, transparent 80px)",
        backgroundSize: "100% 300%", animation: "rbCoil 16s ease-in-out infinite" }} />,
    ],
  },
  {
    id: "crest_rain", name: "Crest Rain", rarity: "legendary", cost: 1,
    lore: "Somewhere above, X'Aol is losing count on purpose.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 0%, #14100a 0%, #0a0810 60%, #050408 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0, opacity: 0.5,
        background: "repeating-linear-gradient(180deg, rgba(200,150,60,0.10) 0px, transparent 8px, transparent 130px, rgba(232,184,75,0.07) 140px)",
        backgroundSize: "100% 140px", animation: "rbRain 6s linear infinite" }} />,
    ],
  },
  {
    id: "jynxis_laughter", name: "J'ynxi's Laughter", rarity: "legendary", cost: 1,
    lore: "Ha. Ha. Ha. (It never fully stops spinning. She won't let it.)",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0, background: "#060310" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: "-30%",
        background: "conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(217,70,239,0.10) 70deg, transparent 140deg, rgba(34,211,238,0.09) 220deg, transparent 300deg, rgba(217,70,239,0.06) 340deg, transparent 360deg)",
        animation: "rbSpin 60s linear infinite" }} />,
    ],
  },
  {
    // ── THE SIGNATURE TEST PIECE ──────────────────────────────
    // First backdrop sold through the Emporium's Signature counter.
    // Deliberately LOUD (crimson + gold, counter-rotating sigil)
    // so it's unmistakable in every letterbox during the
    // consistency test. Cost kept low for beta testing — raise
    // freely later; unlocks are append-only so early buyers keep it.
    id: "molten_sigil", name: "Molten Sigil", rarity: "mythic", cost: 1,
    lore: "Willie stamped the void itself. Warranty voided, obviously.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 55%, #1a0708 0%, #0e040a 55%, #050208 100%)" }} />,
      // Counter-rotating sigil ring — unique motion: rbSigilSpin runs
      // the OPPOSITE direction of J'ynxi's rbSpin, no scale, and is
      // paired with a radial pulse. Not a recolor of anything.
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: "-25%",
        background: "conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(244,63,94,0.14) 40deg, transparent 90deg, rgba(232,184,75,0.12) 150deg, transparent 200deg, rgba(244,63,94,0.10) 260deg, transparent 320deg)",
        animation: "rbSigilSpin 48s linear infinite" }} />,
      <div key="c" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(circle at 50% 55%, transparent 30%, rgba(244,63,94,0.10) 42%, transparent 52%, rgba(232,184,75,0.08) 60%, transparent 70%)",
        animation: "rbSigilPulse 5s ease-in-out infinite" }} />,
    ],
  },
  {
    id: "shatterlight", name: "Shatterlight", rarity: "mythic", cost: 1,
    lore: "What the Shatterfield sees when it closes its eyes.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(160deg, #0a0714 0%, #100a1c 45%, #070510 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "conic-gradient(from 200deg at 30% 70%, transparent, rgba(56,189,248,0.10), transparent 30%), conic-gradient(from 20deg at 70% 30%, transparent, rgba(244,63,94,0.08), transparent 25%), conic-gradient(from 120deg at 55% 55%, transparent, rgba(232,184,75,0.08), transparent 20%)",
        animation: "rbPrism 9s ease-in-out infinite" }} />,
    ],
  },
];

export const getBackdrop = (id) =>
  BACKDROPS.find(b => b.id === id) || BACKDROPS.find(b => b.id === DEFAULT_BACKDROP_ID);

// ── LIVE EQUIPPED HOOK ────────────────────────────────────────
// Failsafe default: no uid / no node / any error → Gilded Obsidian.
export function useEquippedBackdrop(uid) {
  const [equippedId, setEquippedId] = useState(DEFAULT_BACKDROP_ID);
  useEffect(() => {
    if (!uid) { setEquippedId(DEFAULT_BACKDROP_ID); return; }
    const r = ref(db, `profiles/${uid}/roomBackdrops/equipped`);
    const off = onValue(r, (snap) => {
      const v = snap.val();
      setEquippedId(typeof v === "string" && v ? v : DEFAULT_BACKDROP_ID);
    }, () => setEquippedId(DEFAULT_BACKDROP_ID));
    return () => off();
  }, [uid]);
  return equippedId;
}

// ── THE STAGE BAND — the one vertical rectangle where the magic
//    happens (caP'n, July 8). ──────────────────────────────────
// The equipped backdrop no longer washes the WHOLE screen behind
// the room. It is confined to a single vertical band: the desktop
// Plaza's RIGHT bar — the strip that holds MENU / THE PLAZA / the
// crest count (the old symmetric LEFT bar is gone; there's only a
// right band now). All backdrop art + motion lives here so it reads
// as a deliberate stage, not ambient screen wash.
//
// Geometry mirrors Plaza's RECORD_RESERVE math so the band and the
// map's right gap stay in lock-step (one source of truth):
//   width ≈ min(280, 24vw) + 32   (same reserve as the right panel)
//   full viewport height, docked RIGHT (left = vw − width).
// Portrait/mobile: the band spans the full world zone (no side
// bars there) so the backdrop still has a home.
//
// Every backdrop's render() uses inset:0, so clipping the wrapper
// to this band re-homes ALL current and future backdrops with zero
// per-backdrop edits — the overseer pattern.
const STAGE_BAND_MAX_W = 280;   // matches Plaza RECORD panel cap
const STAGE_BAND_VW    = 0.24;  // matches Plaza 24vw reserve
const STAGE_BAND_GAP   = 32;    // matches Plaza panel gap (right:12 + gutter)

export function getStageBand() {
  if (typeof window === "undefined") {
    return { left: 0, top: 0, width: 300, height: 800, portrait: false, side: "right" };
  }
  const vw = window.innerWidth, vh = window.innerHeight;
  const portrait = vh > vw;
  if (portrait) {
    // No side bars in portrait — give the backdrop the full world slice.
    return { left: 0, top: 0, width: vw, height: vh, portrait: true, side: "full" };
  }
  const width = Math.round(Math.min(STAGE_BAND_MAX_W, vw * STAGE_BAND_VW) + STAGE_BAND_GAP);
  // Docked RIGHT — the map fills the space to its left.
  return { left: vw - width, top: 0, width, height: vh, portrait: false, side: "right" };
}

// Live hook — recomputes on resize/orientation, the same triggers
// the rooms use for their own tile math, so band and map stay synced.
export function useStageBand() {
  const [band, setBand] = useState(getStageBand);
  useEffect(() => {
    const update = () => setBand(getStageBand());
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);
  return band;
}

// Feather mask for the band's map-facing edge, so the backdrop melts
// into the void instead of cutting a hard rectangle. Direction keys
// off which side the band is docked: a RIGHT band fades in from its
// LEFT edge (toward the map); a LEFT band fades out on its RIGHT edge;
// portrait/full gets no mask. One helper — both layers share it so
// they can never disagree.
export function bandFeatherMask(side) {
  if (side === "right") {
    // transparent at the left (map side) → solid toward the right
    return "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.35) 8%, #000 28%, #000 100%)";
  }
  if (side === "left") {
    // solid at the left → transparent at the right (map side)
    return "linear-gradient(90deg, #000 0%, #000 72%, rgba(0,0,0,0.35) 92%, transparent 100%)";
  }
  return undefined; // full / portrait — no feather
}

// ── THE BACKDROP STAGE ────────────────────────────────────────
// Render FIRST inside a room's root; the map and HUD sit above it
// (zIndex 0). The equipped backdrop is CLIPPED to the stage band —
// a soft gradient mask feathers its inner edge so it melts into the
// void toward the map instead of cutting a hard rectangle. Pass a
// `band` object to override the geometry for a specific room.
export default function RoomBackdrop({ uid, band }) {
  const liveBand = useStageBand();
  const b = band || liveBand;
  const equippedId = useEquippedBackdrop(uid);
  const bd = getBackdrop(equippedId);
  // Feathers the band's map-facing edge (right band → left edge) so the
  // magic bleeds into the void; portrait fills the full world zone.
  const featherMask = bandFeatherMask(b.side);
  return (
    <div aria-hidden="true" style={{
      position: "absolute",
      left: b.left, top: b.top, width: b.width, height: b.height,
      zIndex: 0, overflow: "hidden", pointerEvents: "none",
      WebkitMaskImage: featherMask, maskImage: featherMask,
    }}>
      {bd.render()}
    </div>
  );
}

// ── THE STAGE — one canonical room rectangle ──────────────────
// Canon geometry is Willie's Workshop: a 20×14 grid, tile = the
// largest whole pixel that fits both axes. EVERY room's play
// rectangle is meant to be exactly this box, centered. Rooms with
// different internal grids (Plaza's 34×20, etc.) get migrated to
// this later — the frame below makes any mismatch VISIBLE now.
export const STAGE_COLS = 20;
export const STAGE_ROWS = 14;

export function getStageRect() {
  if (typeof window === "undefined") {
    return { tile: 32, w: STAGE_COLS * 32, h: STAGE_ROWS * 32, left: 0, top: 0 };
  }
  const vw = window.innerWidth, vh = window.innerHeight;
  const tile = Math.max(1, Math.min(
    Math.floor(vw / STAGE_COLS),
    Math.floor(vh / STAGE_ROWS)
  ));
  const w = STAGE_COLS * tile, h = STAGE_ROWS * tile;
  return { tile, w, h, left: Math.round((vw - w) / 2), top: Math.round((vh - h) / 2) };
}

// Live hook — recomputes on resize/orientation, same triggers the
// rooms already use for their own tile math so the two stay in step.
export function useStageRect() {
  const [rect, setRect] = useState(getStageRect);
  useEffect(() => {
    const update = () => setRect(getStageRect());
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);
  return rect;
}

// ── THE BACKDROP UNDERLAY + STAGE HAIRLINE (overseer layers) ──
// v2 — the bars-on-top approach is DEAD. Lesson learned (July 2026):
// `position: fixed` elements create their own stacking contexts in
// Chrome, so a room's fixed HUD/console (zIndex 60 INSIDE the room's
// root) can never rise above sibling bars painted after the room —
// the bars covered Faction Hall's Record panel. Never stack overlay
// chrome ABOVE a room root and expect the room's z-indexes to win.
//
// New shape, two layers around the children instead of one on top:
//   <BackdropUnderlay/>  — BEFORE children. The equipped backdrop at
//                          z 0, now CONFINED to the stage band (the
//                          right bar) rather than washing the full
//                          viewport — same "one vertical rectangle
//                          where the magic happens" law as Plaza's
//                          RoomBackdrop. Room roots are transparent, so
//                          it shows through the band; every HUD/console/
//                          record still paints above it.
//   <StageHairline/>     — AFTER children. Just the 1px gold outline
//                          of the canonical rectangle (z 30, pointer-
//                          events none) so size-consistency stays
//                          verifiable by eye in every room.
export function BackdropUnderlay({ uid, band }) {
  const liveBand = useStageBand();
  const b = band || liveBand;
  const featherMask = bandFeatherMask(b.side);
  return (
    <div aria-hidden="true" style={{
      position: "fixed",
      left: b.left, top: b.top, width: b.width, height: b.height,
      zIndex: 0, overflow: "hidden", pointerEvents: "none",
      WebkitMaskImage: featherMask, maskImage: featherMask,
    }}>
      <RoomBackdropInner uid={uid} />
    </div>
  );
}

// Inner renderer shared by RoomBackdrop (absolute, in-room) and
// BackdropUnderlay (fixed, overseer) — one pattern source.
function RoomBackdropInner({ uid }) {
  const equippedId = useEquippedBackdrop(uid);
  const bd = getBackdrop(equippedId);
  return <>{bd.render()}</>;
}

export function StageHairline() {
  const rect = useStageRect();
  return (
    <div aria-hidden="true" style={{
      position: "fixed", left: rect.left, top: rect.top,
      width: rect.w, height: rect.h,
      border: `1px solid ${GM.inkGold}33`,
      pointerEvents: "none", zIndex: 30, boxSizing: "border-box",
    }} />
  );
}

// ── THE ATELIER ───────────────────────────────────────────────
// Browse · live-preview · purchase (DC) · equip. Opened from the
// RoomOverlay menu, so every wrapped room can reach it.
export function BackdropAtelier({ profile, onClose }) {
  const uid = profile?.uid || null;

  const [crests, setCrests]     = useState(0);
  const [unlocked, setUnlocked] = useState({});
  const [equipped, setEquipped] = useState(DEFAULT_BACKDROP_ID);
  const [busy, setBusy]         = useState(null);   // backdrop id in flight
  const [notice, setNotice]     = useState(null);   // { text, color }

  // Live reads — DC balance (read-only slice) + backdrop node
  useEffect(() => {
    if (!uid) return;
    const offA = onValue(ref(db, `profiles/${uid}/divineCrests`),
      (s) => setCrests(s.val() || 0));
    const offB = onValue(ref(db, `profiles/${uid}/roomBackdrops`),
      (s) => {
        const v = s.val() || {};
        setUnlocked(v.unlocked || {});
        setEquipped(typeof v.equipped === "string" && v.equipped ? v.equipped : DEFAULT_BACKDROP_ID);
      });
    return () => { offA(); offB(); };
  }, [uid]);

  const isOwned = (bd) => bd.cost === 0 || !!unlocked[bd.id];

  const handleBuy = async (bd) => {
    if (!uid || busy) return;
    setBusy(bd.id); setNotice(null);
    const res = await unlockBackdrop(uid, bd.id, bd.cost, "atelier_purchase");
    if (res.ok) {
      setNotice({ text: `◆ ${bd.name} unlocked.`, color: BACKDROP_RARITY[bd.rarity].color });
    } else if (res.reason === "insufficient-crests") {
      setNotice({ text: `Not enough Divine Crests for ${bd.name}.`, color: "#f43f5e" });
    } else {
      setNotice({ text: "The Atelier hiccuped — nothing was charged. Try again.", color: "#f43f5e" });
    }
    setBusy(null);
  };

  const handleEquip = async (bd) => {
    if (!uid || busy) return;
    setBusy(bd.id);
    await equipBackdrop(uid, bd.id);
    setBusy(null);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 90,
      background: "rgba(3,2,8,0.88)", backdropFilter: "blur(5px)",
      overflowY: "auto", fontFamily: GM.font, color: GM.vellum,
    }}>
      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        background: "linear-gradient(180deg, rgba(5,3,12,0.97), rgba(5,3,12,0.88))",
        borderBottom: `1px solid ${GM.pageBorder}`,
        padding: "14px 18px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 9, letterSpacing: 4, fontWeight: 900, color: `${GM.inkGold}99` }}>
            ◆ CUSTOMIZE
          </div>
          <div style={{ fontSize: 19, fontWeight: 900, color: GM.inkGoldBright, letterSpacing: 1 }}>
            The Backdrop Atelier
          </div>
          <div style={{ fontSize: 10.5, fontStyle: "italic", color: GM.vellumDim, marginTop: 2 }}>
            Dress the void around every room. Equip anywhere, appear everywhere.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            fontSize: 12, fontWeight: 900, color: GM.inkGoldBright,
            letterSpacing: 1, whiteSpace: "nowrap",
          }}>◆ {crests.toLocaleString()}</div>
          <button onClick={onClose} style={{
            padding: "7px 16px", borderRadius: 20, cursor: "pointer",
            border: `1px solid ${GM.inkGold}55`, background: "transparent",
            color: GM.inkGold, fontWeight: 900, fontSize: 11, letterSpacing: 2,
            fontFamily: GM.font,
          }}>✕ CLOSE</button>
        </div>
      </div>

      {/* Notice */}
      {notice && (
        <div style={{
          maxWidth: 860, margin: "12px auto 0", padding: "8px 16px",
          fontSize: 11.5, fontStyle: "italic", color: notice.color,
          textAlign: "center", letterSpacing: 1,
        }}>{notice.text}</div>
      )}

      {/* Catalog grid */}
      <div style={{
        maxWidth: 900, margin: "0 auto", padding: "18px 16px 70px",
        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
        gap: 16,
      }}>
        {BACKDROPS.map(bd => {
          const rar = BACKDROP_RARITY[bd.rarity];
          const owned = isOwned(bd);
          const isEq = equipped === bd.id;
          return (
            <div key={bd.id} style={{
              border: `1px solid ${isEq ? rar.color : GM.pageBorder}`,
              borderRadius: 12, overflow: "hidden",
              background: "rgba(10,8,18,0.85)",
              boxShadow: isEq ? `0 0 18px ${rar.color}33` : "0 4px 14px rgba(0,0,0,0.5)",
            }}>
              {/* Live preview — the backdrop rendering itself, in miniature */}
              <div style={{ position: "relative", height: 110, overflow: "hidden" }}>
                {bd.render()}
                <div style={{
                  position: "absolute", top: 8, left: 8,
                  fontSize: 8, fontWeight: 900, letterSpacing: 2,
                  color: rar.color, textShadow: "0 1px 3px #000",
                }}>{rar.label}</div>
                {isEq && (
                  <div style={{
                    position: "absolute", top: 8, right: 8,
                    fontSize: 8, fontWeight: 900, letterSpacing: 2,
                    color: rar.color, textShadow: "0 1px 3px #000",
                  }}>◆ EQUIPPED</div>
                )}
              </div>

              <div style={{ padding: "10px 12px 12px" }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: GM.vellum }}>{bd.name}</div>
                <div style={{
                  fontSize: 10, fontStyle: "italic", color: GM.vellumDim,
                  marginTop: 3, minHeight: 26, lineHeight: 1.45,
                }}>{bd.lore}</div>

                <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                  {!owned ? (
                    <button
                      onClick={() => handleBuy(bd)}
                      disabled={busy === bd.id || crests < bd.cost}
                      style={{
                        flex: 1, padding: "7px 0", borderRadius: 18,
                        cursor: crests < bd.cost ? "not-allowed" : "pointer",
                        border: `1px solid ${rar.color}66`,
                        background: "transparent",
                        color: crests < bd.cost ? GM.vellumFaint : rar.color,
                        fontFamily: GM.font, fontSize: 10.5, fontWeight: 900, letterSpacing: 1.5,
                        opacity: busy === bd.id ? 0.5 : 1,
                      }}
                    >
                      {busy === bd.id ? "…" : `UNLOCK — ◆ ${bd.cost.toLocaleString()}`}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEquip(bd)}
                      disabled={isEq || busy === bd.id}
                      style={{
                        flex: 1, padding: "7px 0", borderRadius: 18,
                        cursor: isEq ? "default" : "pointer",
                        border: `1px solid ${isEq ? GM.pageBorder : GM.inkGold + "66"}`,
                        background: isEq ? "rgba(232,184,75,0.06)" : "transparent",
                        color: isEq ? GM.vellumDim : GM.inkGoldBright,
                        fontFamily: GM.font, fontSize: 10.5, fontWeight: 900, letterSpacing: 1.5,
                        opacity: busy === bd.id ? 0.5 : 1,
                      }}
                    >
                      {isEq ? "WEARING THE VOID" : "EQUIP"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
